import type { FunctionComponent } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { FormattedDate, FormattedNumber } from 'react-intl';

import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Gap } from '../../../shared/components/gap/gap';
import { Icon } from '../../../shared/components/icon/icon';
import { IconHover } from '../../../shared/components/icon-hover/icon-hover';
import { Loader } from '../../../shared/components/loader/loader';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { ensureIsoDateWithTimeZone } from '../../../shared/utils/date';
import { capitalize, slugify } from '../../../shared/utils/strings';
import type { IUserTransaction } from '../../queries/transactions.graphql';

import { SportsBetInformation } from './sports-bet-information/sports-bet-information';
import styles from './sports-transactions.module.scss';

const style = createStyleHelper(styles, 'sports-transactions');

export interface ISportsAdditionalProperties {
    EventEndDate?: string;
    EventId?: string;
    EventStartDate?: string;
    IsLive?: string;
    MarketName?: string;
    Odds?: string;
    SelectionName?: string;
    Size?: string;
    SportName?: string;
    Stake?: string;
}

export interface ITransactionsProps {
    className?: string;
    activityData: IUserTransaction[];
    onLoadMore: () => void;
    hasMore: boolean;
    fetching: boolean;
}

export const SportsTransactionsComponent: FunctionComponent<ITransactionsProps> = ({
    className,
    activityData,
    onLoadMore,
    hasMore,
    fetching,
}) => {
    const [sentryRef] = useInfiniteScroll({
        loading: fetching,
        hasNextPage: hasMore,
        onLoadMore,
        disabled: activityData.length < 1 ? true : false,
        rootMargin: '0px 0px 400px 0px',
    });

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('table', ['heading'])}>
                <div className={style('date-and-time')}>
                    <FormattedMessage
                        id="account.sports-transactions.date-and-time"
                        defaultMessage="Date and Time"
                    />
                </div>
                <div className={style('bet-details')}>
                    <FormattedMessage
                        id="account.sports-transactions.bet-details"
                        defaultMessage="Bet Details"
                    />
                </div>
                <div className={style('transaction-details')}>
                    <FormattedMessage
                        id="account.sports-transactions.transaction-details"
                        defaultMessage="Transaction"
                    />
                </div>
                <div className={style('wallet')}>
                    <FormattedMessage
                        id="account.sports-transactions.wallet"
                        defaultMessage="Wallet"
                    />
                </div>
                <div className={style('before')}>
                    <FormattedMessage
                        id="account.sports-transactions.before"
                        defaultMessage="Before"
                    />
                </div>
                <div className={style('transaction')}>
                    <FormattedMessage
                        id="account.sports-transactions.transaction"
                        defaultMessage="Transaction"
                    />
                </div>
                <div className={style('after')}>
                    <FormattedMessage
                        id="account.sports-transactions.after"
                        defaultMessage="After"
                    />
                </div>
            </div>
            {activityData.length
                ? activityData.map(
                      ({
                          additionalProperties,
                          transactionId,
                          lastUpdatedOn,
                          transactionAmount,
                          walletType,
                          currencyCode,
                          previousBalanceAmount,
                          balanceAmount,
                          transactionBatchId,
                          ipAddress,
                          geoCountry,
                          geoCity,
                          geoRegion,
                      }) => {
                          if (!currencyCode) {
                              return;
                          }

                          return (
                              <div
                                  className={style('table')}
                                  key={`${transactionId}.${lastUpdatedOn}`}
                              >
                                  <div className={style('date-and-time')}>
                                      <IconHover
                                          hoverItem={
                                              <Gap direction="horizontal">
                                                  <FormattedDate
                                                      value={
                                                          lastUpdatedOn
                                                              ? ensureIsoDateWithTimeZone(
                                                                    lastUpdatedOn,
                                                                )
                                                              : ''
                                                      }
                                                      dateStyle={'short'}
                                                      timeStyle={'short'}
                                                  />
                                                  <Icon name={'info-circle'} />
                                              </Gap>
                                          }
                                          infoDisplay={
                                              <FormattedMessage
                                                  id="account.transactions.wallet.info"
                                                  defaultMessage="IP Address: {ipAddress}<br></br>Geo Country: {geoCountry}<br></br>Geo City: {geoCity}<br></br>Geo Region: {geoRegion}"
                                                  values={{
                                                      ipAddress,
                                                      geoCountry,
                                                      geoCity,
                                                      geoRegion,
                                                  }}
                                              />
                                          }
                                      />
                                  </div>
                                  <div className={style('bet-details')}>
                                      <SportsBetInformation properties={additionalProperties} />
                                  </div>
                                  <div className={style('transaction-details')}>
                                      <div>
                                          <FormattedMessage
                                              id="account.sports-transactions.before.short"
                                              defaultMessage="Before: "
                                          />{' '}
                                          <FormattedNumber
                                              value={previousBalanceAmount}
                                              currency={currencyCode}
                                              style={'currency'}
                                          />
                                      </div>
                                      <div>
                                          <FormattedMessage
                                              id="account.sports-transactions.transaction.short"
                                              defaultMessage="Value: "
                                          />{' '}
                                          <FormattedNumber
                                              value={transactionAmount}
                                              currency={currencyCode}
                                              style={'currency'}
                                          />
                                      </div>
                                      <div>
                                          <FormattedMessage
                                              id="account.sports-transactions.after.short"
                                              defaultMessage="After: "
                                          />{' '}
                                          <FormattedNumber
                                              value={balanceAmount}
                                              currency={currencyCode}
                                              style={'currency'}
                                          />
                                      </div>
                                  </div>
                                  <div className={style('wallet')}>
                                      <FormattedMessage
                                          id={`account.sports-transactions.wallet.${slugify(
                                              walletType || 'none',
                                          )}`}
                                          defaultMessage={capitalize(walletType || 'none')}
                                      />
                                  </div>
                                  <div className={style('before')}>
                                      <FormattedNumber
                                          value={previousBalanceAmount}
                                          currency={currencyCode}
                                          style={'currency'}
                                      />
                                  </div>
                                  <div className={style('transaction')}>
                                      <IconHover
                                          hoverItem={
                                              <Gap direction="horizontal">
                                                  <FormattedNumber
                                                      value={transactionAmount}
                                                      currency={currencyCode}
                                                      style={'currency'}
                                                  />
                                                  <Icon name={'info-circle'} />
                                              </Gap>
                                          }
                                          infoDisplay={
                                              <FormattedMessage
                                                  id="account.transactions.wallet.info"
                                                  defaultMessage="Transaction Batch Id: {batchId} <br></br>Transaction Id: {transactionId}"
                                                  values={{
                                                      batchId: transactionBatchId || 'N/A',
                                                      transactionId: transactionId || 'N/A',
                                                  }}
                                              />
                                          }
                                      />
                                  </div>
                                  <div className={style('after')}>
                                      <FormattedNumber
                                          value={balanceAmount}
                                          currency={currencyCode}
                                          style={'currency'}
                                      />
                                  </div>
                              </div>
                          );
                      },
                  )
                : null}

            {fetching && activityData.length === 0 ? (
                <div className={style('message')}>
                    <Loader />
                </div>
            ) : null}

            {!activityData.length ? (
                <div className={style('message')}>
                    <FormattedMessage
                        id="account.sports-transactions.no-data-to-display"
                        defaultMessage="No data to display"
                    />
                </div>
            ) : null}

            {hasMore && activityData.length > 0 && !fetching ? (
                <div className={style('load-more')} ref={sentryRef}>
                    <Icon name={'caret-down'} />
                </div>
            ) : null}
        </div>
    );
};
